import { Link } from 'react-router-dom';
import { CheckCircle2, ChevronRight, Lock } from 'lucide-react';
import type { ModuleSummary } from '@/lib/courseView';
import { titleGradient } from '@/lib/utils';

/**
 * One module in the course page's module list: 80×56 thumb (the module image,
 * or its deterministic gradient), title, and the same
 * `x/y Lektionen` · `n%` · bar idiom the Home course cards use — so progress
 * reads identically at course, module and lesson level.
 *
 * Deliberately ~88px tall (56px thumb + p-4) so all 15 Gold modules fit in
 * about three phone screens. The module description is NOT here; it lives on
 * the module page.
 *
 * A locked module — every lesson inside drip-locked — keeps the anatomy but is
 * inert, greyed and says why, mirroring a locked lesson row.
 */
export function ModuleRow({ summary, to }: { summary: ModuleSummary; to: string }) {
  const { module, totalLessons, completedLessons, pct, completed, locked } = summary;

  const inner = (
    <>
      {module.imageUrl ? (
        <img
          src={module.imageUrl}
          alt=""
          draggable={false}
          className={
            locked
              ? 'h-14 w-20 shrink-0 rounded-md object-cover opacity-50 grayscale'
              : 'h-14 w-20 shrink-0 rounded-md object-cover'
          }
        />
      ) : (
        <div
          className={
            locked
              ? 'h-14 w-20 shrink-0 rounded-md opacity-50 grayscale'
              : 'h-14 w-20 shrink-0 rounded-md'
          }
          style={{ backgroundImage: titleGradient(module.title) }}
        />
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div
          className={
            locked
              ? 'line-clamp-1 text-sm font-medium leading-snug text-muted-foreground'
              : 'line-clamp-1 text-sm font-medium leading-snug text-foreground'
          }
        >
          {module.title}
        </div>
        {locked ? (
          <div className="text-xs text-muted-foreground">
            Vorherige Module zuerst abschließen
          </div>
        ) : (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                {completedLessons}/{totalLessons} Lektionen
              </span>
              <span>{pct}%</span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
              <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}
      </div>
      {locked ? (
        <Lock className="size-4 shrink-0 text-muted-foreground" />
      ) : completed ? (
        <CheckCircle2 className="size-5 shrink-0 text-success" />
      ) : (
        <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
      )}
    </>
  );

  if (locked) {
    return (
      <div
        aria-disabled
        title="Schließe zuerst die vorherigen Module ab."
        className="flex cursor-not-allowed items-center gap-4 rounded-xl border border-dashed bg-muted/40 p-4"
      >
        {inner}
      </div>
    );
  }
  return (
    <Link
      to={to}
      className="flex items-center gap-4 rounded-xl border bg-card p-4 text-card-foreground shadow-soft transition duration-200 hover:border-primary/40 hover:shadow-lift"
    >
      {inner}
    </Link>
  );
}
